import React, { useState, useRef, useEffect } from "react";
import "./FloatingChatBot.css";
import { FiSend, FiX, FiMaximize2, FiMinimize2 } from "react-icons/fi";
import { IoIosChatboxes } from "react-icons/io";
import ReactMarkdown from "react-markdown";

const FloatingChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: "bot",
      text: "Ask your questions about PubVision platform, features, navigation, or how to use the tool. I'm here to help you make the most of your Scientific research experience."
    }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [position, setPosition] = useState(() => {
    const saved = localStorage.getItem("chatbotPosition");
    return saved ? JSON.parse(saved) : { bottom: 24, right: 24 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [chatWindowPosition, setChatWindowPosition] = useState({ bottom: 70, right: 0 });
  const messagesEndRef = useRef(null);
  const dragRef = useRef({ isDragging: false, offset: { x: 0, y: 0 } });
  const token = localStorage.getItem("authToken");
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // eslint-disable-next-line complexity
  useEffect(() => {
    // Recalculate chat window position on window resize
    const handleResize = () => {
      if (isOpen) {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const { chatWidth, chatHeight } = getChatDimensions();

        const newChatPosition = calculatePosition(
          windowWidth, 
          windowHeight, 
          chatWidth, 
          chatHeight, 
          position.bottom, 
          position.right
        );

        setChatWindowPosition(newChatPosition);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, position]);

  const toggleChat = () => {
    if (!isOpen) {
      // Calculate optimal position for chat window when opening
      requestAnimationFrame(() => calculateChatWindowPosition());
    } else {
      setIsMaximized(false); // Reset maximize state when closing
    }
    setIsOpen(!isOpen);
  };

  const getChatDimensions = () => {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    let chatWidth = 420;
    let chatHeight = 520;
    
    if (windowWidth <= 480) {
      chatWidth = Math.min(windowWidth - 32, 420);
      chatHeight = Math.min(windowHeight - 100, 600);
    } else if (windowWidth <= 520) {
      chatWidth = Math.min(windowWidth - 48, 420);
    }
    
    return { chatWidth, chatHeight };
  };

  const calculatePosition = (windowWidth, windowHeight, chatWidth, chatHeight, buttonBottom, buttonRight) => {
    const buttonSize = 48;
    const margin = 16;
    
    // Calculate button's actual position on screen
    const buttonTopPosition = windowHeight - buttonBottom - buttonSize;
    const buttonLeftPosition = windowWidth - buttonRight - buttonSize;
    
    // Calculate available space in all directions
    const spaceAbove = buttonTopPosition;
    const spaceBelow = windowHeight - buttonTopPosition - buttonSize;
    const spaceRight = windowWidth - buttonLeftPosition - buttonSize;
    const spaceLeft = buttonLeftPosition;

    let newChatPosition = { bottom: 70, right: 0 };

    // Determine vertical position - open where there's more space
    const shouldOpenBelow = spaceBelow > spaceAbove;
    
    if (shouldOpenBelow) {
      // Open below the button
      if (spaceBelow >= chatHeight + margin) {
        // Enough space below
        newChatPosition.bottom = -(chatHeight + margin);
      } else {
        // Not enough space, position as low as possible
        newChatPosition.bottom = -(spaceBelow - margin);
      }
    } else {
      // Open above the button (default)
      if (spaceAbove >= chatHeight + margin) {
        // Enough space above
        newChatPosition.bottom = buttonSize + margin;
      } else {
        // Not enough space, position as high as possible
        const maxBottom = windowHeight - chatHeight - margin;
        newChatPosition.bottom = maxBottom - buttonTopPosition;
      }
    }

    // Determine horizontal position - open where there's more space
    const shouldOpenRight = spaceRight > spaceLeft;
    
    if (shouldOpenRight) {
      // Open to the right of the button
      if (spaceRight >= chatWidth + margin) {
        // Enough space on the right
        newChatPosition.right = -(chatWidth + margin - buttonSize);
      } else {
        // Not enough space, fit within available space
        newChatPosition.right = -(spaceRight - margin);
      }
    } else {
      // Open to the left of the button (align with right edge)
      if (spaceLeft >= chatWidth - buttonSize) {
        // Enough space on the left
        newChatPosition.right = 0;
      } else {
        // Not enough space, fit within available space
        const minRight = -(windowWidth - chatWidth - margin - buttonLeftPosition);
        newChatPosition.right = Math.max(minRight, 0);
      }
    }

    return newChatPosition;
  };

  const calculateChatWindowPosition = () => {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const { chatWidth, chatHeight } = getChatDimensions();

    const newChatPosition = calculatePosition(
      windowWidth, 
      windowHeight, 
      chatWidth, 
      chatHeight, 
      position.bottom, 
      position.right
    );

    setChatWindowPosition(newChatPosition);
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = input.trim();
    setMessages(prev => [...prev, { type: "user", text: userMessage }]);
    setInput("");
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("user_msg", userMessage);

      const response = await fetch(`${BASE_URL}/chat_assistant`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to get response");
      }

      const data = await response.json();
      setMessages(prev => [...prev, { 
        type: "bot", 
        text: data.response || data.answer || "I'm sorry, I couldn't process your request. Please try again."
      }]);
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prev => [...prev, { 
        type: "bot", 
        text: "I'm sorry, I encountered an error. Please try again later."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Drag handlers
  const handleMouseDown = (e) => {
    if (isOpen) return; // Don't allow dragging when chat is open
    e.preventDefault();
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    const offset = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
    dragRef.current = { isDragging: true, offset };
  };

  const handleTouchStart = (e) => {
    if (isOpen) return;
    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    const offset = {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    };
    dragRef.current = { isDragging: true, offset };
    setIsDragging(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!dragRef.current.isDragging) return;
      
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const buttonSize = 48;
      
      // Calculate new position from cursor
      let newBottom = windowHeight - e.clientY - (buttonSize - dragRef.current.offset.y);
      let newRight = windowWidth - e.clientX - (buttonSize - dragRef.current.offset.x);
      
      // Bounds checking - keep button visible on screen
      newBottom = Math.max(8, Math.min(windowHeight - buttonSize - 8, newBottom));
      newRight = Math.max(8, Math.min(windowWidth - buttonSize - 8, newRight));
      
      setPosition({ bottom: newBottom, right: newRight });
    };

    const handleTouchMove = (e) => {
      if (!dragRef.current.isDragging) return;
      e.preventDefault();
      
      const touch = e.touches[0];
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const buttonSize = 48;
      
      let newBottom = windowHeight - touch.clientY - (buttonSize - dragRef.current.offset.y);
      let newRight = windowWidth - touch.clientX - (buttonSize - dragRef.current.offset.x);
      
      newBottom = Math.max(8, Math.min(windowHeight - buttonSize - 8, newBottom));
      newRight = Math.max(8, Math.min(windowWidth - buttonSize - 8, newRight));
      
      setPosition({ bottom: newBottom, right: newRight });
    };

    const handleMouseUp = () => {
      if (dragRef.current.isDragging) {
        dragRef.current.isDragging = false;
        setIsDragging(false);
        setPosition(prev => {
          localStorage.setItem("chatbotPosition", JSON.stringify(prev));
          return prev;
        });
      }
    };

    const handleTouchEnd = () => {
      handleMouseUp();
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove, { passive: false });
      document.addEventListener("touchend", handleTouchEnd);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [isDragging]);

  return (
    <div 
      className={`floating-chatbot-container ${isDragging ? "dragging" : ""}`}
      style={{
        bottom: `${position.bottom}px`,
        right: `${position.right}px`
      }}
    >
      {/* Chat Window */}
      {isOpen && (
        <>
          <div 
            className={`chatbot-window ${isMaximized ? "maximized" : ""}`}
            style={isMaximized ? {} : {
              bottom: `${chatWindowPosition.bottom}px`,
              right: `${chatWindowPosition.right}px`
            }}
          >
            {/* Header */}
            <div className="chatbot-header">
              <div className="chatbot-header-content">
                <h3>PUBBY</h3>
                <p>Ask Questions about the tool</p>
              </div>
              <div className="chatbot-header-actions">
                <button className="chatbot-action-btn" onClick={() => setIsMaximized(!isMaximized)} title={isMaximized ? "Restore" : "Maximize"}>
                  {isMaximized ? <FiMinimize2 /> : <FiMaximize2 />}
                </button>
                <button className="chatbot-action-btn" onClick={toggleChat} title="Close">
                  <FiX />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="chatbot-messages">
              {messages.map((msg, idx) => (
                <div key={idx} className={`chatbot-message ${msg.type}`}>
                  <div className="chatbot-bubble">
                    {msg.type === "bot" ? (
                      <ReactMarkdown>{msg.text}</ReactMarkdown>
                    ) : (
                      msg.text
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="chatbot-message bot">
                  <div className="chatbot-bubble">
                    <div className="chatbot-typing">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="chatbot-input-container">
              <div className="chatbot-input-wrapper">
                <input
                  type="text"
                  placeholder="Type your question here"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                />
                <button 
                  className="chatbot-send-btn" 
                  onClick={handleSend}
                  disabled={isLoading || !input.trim()}
                >
                  <FiSend />
                </button>
              </div>
            </div>
          </div>

          {/* Minimize Button - Outside the chat window */}
          {!isMaximized && (
            <button className="chatbot-minimize-btn" onClick={toggleChat}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7" />
              </svg>
            </button>
          )}
        </>
      )}

      {/* Floating Button */}
      <button 
        className={`floating-chat-btn ${isOpen ? "hidden" : ""}`}
        onClick={toggleChat}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        title={isDragging ? "Release to drop" : "PUBBY - Click to chat, Drag to move"}
        style={{ cursor: isDragging ? "grabbing" : "grab" }}
      >
        <IoIosChatboxes size={22} color="#fff" />
      </button>
    </div>
  );
};

export default FloatingChatBot;
